import { useCallback, useMemo, useState } from 'react';
import { isAfter, isBefore, isValid, parseISO } from 'date-fns';

export type MemberFields = {
  Title: string;
  FirstName: string;
  LastName: string;
  DateOfBirth: string;
};

export interface MemberFieldErrors {
  Title: string | null;
  FirstName: string | null;
  LastName: string | null;
  DateOfBirth: string | null;
}

export type TouchedMemberFields = Record<keyof MemberFieldErrors, boolean>;

export const TITLE_OPTIONS = ['Mr', 'Mrs', 'Ms', 'Miss', 'Sir', 'Doctor', 'Mx'] as const;

export const NAME_MAX_LENGTH = 50;

// Letters, spaces, apostrophes and hyphens - so O'Brien and Smith-Jones pass, digits don't.
const NAME_REGEX = /^[a-zA-Z '-]+$/;

export const emptyMemberFields: MemberFields = { Title: '', FirstName: '', LastName: '', DateOfBirth: '' };

/**
 * Upper-cases the first letter of each part of a name, where a part starts at the string start
 * or after a space, hyphen or apostrophe - so "mary-jane o'brien" reads "Mary-Jane O'Brien".
 * Only a lower-case letter is rewritten, which leaves names carrying their own capitals
 * ("McDonald", "JOHN") exactly as typed.
 */
export const capitaliseName = (value: string): string => value.replace(/(^|[\s'-])([a-z])/g, (_, boundary: string, letter: string) => `${boundary}${letter.toUpperCase()}`);

export const validateName = (value: string): string | null => {
  if (!value) return 'Required';
  if (value.length > NAME_MAX_LENGTH) return `Must be ${NAME_MAX_LENGTH} characters or fewer`;
  if (!NAME_REGEX.test(value)) return 'Letters, apostrophes and hyphens only';
  return null;
};

// The earliest date of birth the form accepts, as an ISO date so it can be handed straight to
// the date input's "min" attribute as well as to the validator.
export const MIN_DATE_OF_BIRTH = '1909-01-01';

export const validateDateOfBirth = (value: string): string | null => {
  if (!value) return 'Required';
  const parsed = parseISO(value);
  if (!isValid(parsed)) return 'Enter a valid date';
  // No upper age limit is applied, but a birth date can't be in the future, nor earlier
  // than MIN_DATE_OF_BIRTH.
  if (isAfter(parsed, new Date())) return 'Date of birth cannot be in the future';
  if (isBefore(parsed, parseISO(MIN_DATE_OF_BIRTH))) return 'Date of birth cannot be before 01/01/1909';
  return null;
};

export const validateMemberFields = (fields: MemberFields): MemberFieldErrors => ({
  Title: !fields.Title ? 'Required' : null,
  FirstName: validateName(fields.FirstName),
  LastName: validateName(fields.LastName),
  DateOfBirth: validateDateOfBirth(fields.DateOfBirth),
});

/**
 * The member form state machine, shared by the add and the edit journeys - the mirror of
 * useVehicleForm. Add seeds it with emptyMemberFields, edit seeds it from the member being
 * edited, and everything downstream of that is identical.
 *
 * "initialFields" is read once, on mount. Both modals are unmounted between openings, so a
 * reopened modal reseeds; nothing here reacts to the prop changing under a mounted form.
 */
const useMemberForm = (initialFields: MemberFields) => {
  const [fields, setFields] = useState<MemberFields>(initialFields);
  const [touched, setTouched] = useState<TouchedMemberFields>({
    Title: false,
    FirstName: false,
    LastName: false,
    DateOfBirth: false,
  });

  const errors = useMemo(() => validateMemberFields(fields), [fields]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    // Names are capitalised as they are typed, so the field always shows what will be saved.
    const nextValue = name === 'FirstName' || name === 'LastName' ? capitaliseName(value) : value;
    setFields((prev) => ({ ...prev, [name]: nextValue }));
    setTouched((prev) => ({ ...prev, [name]: true }));
  }, []);

  const handleBlur = useCallback((fieldName: keyof MemberFieldErrors) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
  }, []);

  const hasErrors = Object.values(errors).some(Boolean);

  return { fields, errors, touched, hasErrors, setTouched, handleChange, handleBlur };
};

export default useMemberForm;
